import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { checkRole, UserRole } from '../../../utils/roleHelpers'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { getToken, removeToken } from '../../../utils/auth'
import { isTokenExpired } from '../../../utils/tokenCheck'

interface ProtectedRouteProps {
  readonly children: ReactNode
  readonly requiredRoles?: UserRole[]
  readonly redirectTo?: string
}

export default function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAppSelector(
    state => state.auth
  )
  const location = useLocation()
  const navigate = useNavigate()

  // Check token expiry on mount and when location changes - redirect immediately
  useEffect(() => {
    const token = getToken()

    if (token) {
      const expired = isTokenExpired(token)

      if (expired) {
        removeToken()
        navigate(redirectTo, { replace: true, state: { from: location } })
        return
      }
    }
  }, [location.pathname, navigate, redirectTo, location])

  // Check token expiry before checking authentication (synchronous check)
  const token = getToken()

  if (token) {
    const expired = isTokenExpired(token)

    if (expired) {
      removeToken()
      // Redirect to login immediately
      return <Navigate to={redirectTo} state={{ from: location }} replace />
    }
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />
  }

  // Redirect to login if not authenticated (this now also checks token expiry via isAuthenticated)
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Check role-based access if required roles are specified
  if (requiredRoles && requiredRoles.length > 0) {
    // Map 'user' role to 'viewer' for role checking (they are equivalent)
    const userRole = user.role === 'user' ? 'viewer' : user.role
    const hasAccess = checkRole(userRole ?? null, requiredRoles)

    if (!hasAccess) {
      // Redirect to 403 page or home with error
      return <Navigate to='/403' state={{ from: location }} replace />
    }
  }

  return <>{children}</>
}
